import { useState } from 'react';
import { Button } from './ui/Button';
import { IconButton } from './ui/IconButton';
import { Icon } from './ui/Icon';
import { useToast } from '../contexts/ToastContext';
import { useAuthStore } from '../store/authStore';
import { authErrorMessage } from '../services/authErrors';
import type { AuthSessionInfo } from '../services/authSession';
import UpgradeAccountModal from './auth/UpgradeAccountModal';
import GuestSignInIdPanel from './auth/GuestSignInIdPanel';
import { Modal } from './ui/Modal';
import { readDismissedAccountNotice, writeDismissedAccountNotice } from '../utils/accountNoticeDismissal';

type NoticeKind = 'guest' | 'verify';

function noticeFor(session: AuthSessionInfo | null): NoticeKind | null {
    if (!session) return null;
    if (session.isGuest) return 'guest';
    if (session.usesPassword && !session.emailVerified) return 'verify';
    return null;
}

function dismissalKey(kind: NoticeKind, session: AuthSessionInfo): string {
    return `${kind}:${session.uid}:${session.email ?? ''}`;
}

/**
 * Nudges guests to create an account and password accounts to verify their
 * email (issue #311). Never blocks play; dismissing hides that notice for the
 * rest of the browser session.
 */
export default function AccountNoticeBanner() {
    const { session, resendVerificationEmail, refreshSession } = useAuthStore();
    const { show } = useToast();
    const [dismissed, setDismissed] = useState(readDismissedAccountNotice);
    const [upgradeOpen, setUpgradeOpen] = useState(false);
    const [guestIdOpen, setGuestIdOpen] = useState(false);
    const [busy, setBusy] = useState<'resend' | 'refresh' | null>(null);

    const kind = noticeFor(session);
    const key = kind && session ? dismissalKey(kind, session) : null;
    const modal = (
        <>
            <UpgradeAccountModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />
            <Modal open={guestIdOpen} onClose={() => setGuestIdOpen(false)} title="Your guest ID">
                <GuestSignInIdPanel />
            </Modal>
        </>
    );
    if (!kind || !key || dismissed === key) return modal;

    const dismiss = () => {
        writeDismissedAccountNotice(key);
        setDismissed(key);
    };

    const resend = async () => {
        setBusy('resend');
        try {
            await resendVerificationEmail();
            show('Verification email sent. Check your inbox.', { tone: 'success' });
        } catch (error) {
            show(authErrorMessage(error), { tone: 'danger' });
        } finally {
            setBusy(null);
        }
    };

    const checkVerified = async () => {
        setBusy('refresh');
        try {
            const refreshed = await refreshSession();
            if (refreshed?.emailVerified) {
                show('Email verified.', { tone: 'success' });
            } else {
                show("Your email isn't verified yet. Open the link we sent you, then try again.", { tone: 'warning' });
            }
        } catch (error) {
            show(authErrorMessage(error), { tone: 'danger' });
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="mx-auto w-full max-w-6xl px-4 pt-3">
            <div
                data-testid="account-notice"
                role="status"
                className="flex flex-wrap items-center gap-3 rounded-tile border border-status-warning/40 bg-status-warning/15 px-4 py-3"
            >
                <Icon name="alert" size={20} className="shrink-0 text-status-warning" />
                <p className="min-w-0 flex-1 text-body text-content-primary">
                    {kind === 'guest' ? (
                        <>
                            You&apos;re playing as a guest. Save your guest ID to get back in later, or create an account to
                            keep your progress for good.
                        </>
                    ) : (
                        <>
                            Verify your email address. We sent a link to{' '}
                            <span className="font-semibold">{session?.email}</span>.
                        </>
                    )}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                    {kind === 'guest' ? (
                        <>
                            <Button type="button" size="sm" variant="secondary" onClick={() => setGuestIdOpen(true)}>
                                Guest ID
                            </Button>
                            <Button type="button" size="sm" onClick={() => setUpgradeOpen(true)}>
                                Create account
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button type="button" size="sm" variant="secondary" loading={busy === 'resend'} disabled={busy !== null} onClick={resend}>
                                Resend email
                            </Button>
                            <Button type="button" size="sm" variant="ghost" loading={busy === 'refresh'} disabled={busy !== null} onClick={checkVerified}>
                                I&apos;ve verified
                            </Button>
                        </>
                    )}
                    <IconButton aria-label="Dismiss" variant="ghost" onClick={dismiss}>
                        <Icon name="close" size={14} />
                    </IconButton>
                </div>
            </div>
            {modal}
        </div>
    );
}
